import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../auth/AuthContext";
import { fetchProfile, upsertProfile } from "../api/profiles";
import {
  DEFAULT_SUGGESTION_FONT_SIZE,
  DEFAULT_SUGGESTION_HIGHLIGHT,
  LANGUAGE_OPTIONS,
  MEDICAL_SPECIALTIES,
  ROLE_OPTIONS,
  SUGGESTION_FONT_SIZE_OPTIONS,
  SUGGESTION_HIGHLIGHT_OPTIONS,
  normalizeProfileLanguage,
} from "../constants/profileOptions";
import { useLanguage } from "../i18n/LanguageContext";

const defaultDisplayName = (email) => {
  const localPart = email?.split("@")[0]?.trim();
  if (!localPart) return "";
  return localPart
    .split(/[._-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const ProfilePage = () => {
  const { user } = useAuth();
  const { portal, setLanguageFromProfile } = useLanguage();
  const copy = portal.profile;
  const common = portal.common;
  const [form, setForm] = useState({
    display_name: "",
    role: "",
    specialty: "",
    language: "",
    clinic_name: "",
    suggestion_highlight: DEFAULT_SUGGESTION_HIGHLIGHT,
    suggestion_font_size: DEFAULT_SUGGESTION_FONT_SIZE,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const profile = await fetchProfile();
      setForm({
        display_name:
          profile?.display_name || defaultDisplayName(user?.email) || "",
        role: profile?.role || "",
        specialty: profile?.specialty || "",
        language: normalizeProfileLanguage(profile?.language) || "",
        clinic_name: profile?.clinic_name || "",
        suggestion_highlight:
          profile?.suggestion_highlight || DEFAULT_SUGGESTION_HIGHLIGHT,
        suggestion_font_size:
          profile?.suggestion_font_size || DEFAULT_SUGGESTION_FONT_SIZE,
      });
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setForm((current) => ({
        ...current,
        display_name: defaultDisplayName(user?.email),
      }));
    } finally {
      setLoading(false);
    }
  }, [user?.email, copy.loadFailed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isValid = useMemo(() => {
    const name = form.display_name.trim();
    return name.length >= 2 && name.length <= 80;
  }, [form.display_name]);

  const handleChange = (field, value) => {
    setSuccess("");
    setError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid) {
      setError(copy.nameInvalid);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await upsertProfile({
        display_name: form.display_name.trim(),
        role: form.role || undefined,
        specialty: form.specialty.trim() || undefined,
        language: form.language || undefined,
        clinic_name: form.clinic_name.trim() || undefined,
        suggestion_highlight:
          form.suggestion_highlight || DEFAULT_SUGGESTION_HIGHLIGHT,
        suggestion_font_size:
          form.suggestion_font_size || DEFAULT_SUGGESTION_FONT_SIZE,
      });
      setLanguageFromProfile(form.language);
      setSuccess(copy.saved);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell title={copy.title} description={copy.description}>
      {error && <div className="app-alert">{error}</div>}
      {success && <div className="app-alert success">{success}</div>}

      <section className="app-form-card">
        <h2>{copy.detailsTitle}</h2>
        {loading ? (
          <p className="app-hint">{copy.loadingProfile}</p>
        ) : (
          <form onSubmit={handleSubmit} className="app-form">
            <label htmlFor="display-name">{copy.displayName}</label>
            <input
              id="display-name"
              type="text"
              value={form.display_name}
              onChange={(e) => handleChange("display_name", e.target.value)}
              required
              disabled={saving}
            />

            <label htmlFor="role">{copy.role}</label>
            <select
              id="role"
              value={form.role}
              onChange={(e) => handleChange("role", e.target.value)}
              disabled={saving}
            >
              <option value="">{copy.selectRole}</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {copy.roles[role] || role}
                </option>
              ))}
            </select>

            <label htmlFor="specialty">{copy.specialty}</label>
            <select
              id="specialty"
              value={form.specialty}
              onChange={(e) => handleChange("specialty", e.target.value)}
              disabled={saving}
            >
              <option value="">{copy.selectSpecialty}</option>
              {MEDICAL_SPECIALTIES.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {copy.specialties[specialty] || specialty}
                </option>
              ))}
            </select>

            <label htmlFor="language">{copy.language}</label>
            <select
              id="language"
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              disabled={saving}
            >
              <option value="">{copy.selectLanguage}</option>
              {LANGUAGE_OPTIONS.map((language) => (
                <option key={language.code} value={language.code}>
                  {copy.contentLanguages[language.code] || language.label}
                </option>
              ))}
            </select>

            <label htmlFor="clinic">{copy.clinicName}</label>
            <input
              id="clinic"
              type="text"
              value={form.clinic_name}
              onChange={(e) => handleChange("clinic_name", e.target.value)}
              placeholder={copy.clinicPlaceholder}
              disabled={saving}
            />

            <label>{copy.highlightLabel}</label>
            <div className="app-highlight-group">
              {SUGGESTION_HIGHLIGHT_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={
                    form.suggestion_highlight === option.id
                      ? "app-highlight-option selected"
                      : "app-highlight-option"
                  }
                  onClick={() =>
                    handleChange("suggestion_highlight", option.id)
                  }
                  disabled={saving}
                >
                  <span
                    className="app-swatch"
                    style={{
                      backgroundColor: option.bgcolor,
                      borderColor: option.borderColor,
                    }}
                  />
                  {copy.highlights[option.id] || option.label}
                </button>
              ))}
            </div>

            <label>{copy.fontSizeLabel}</label>
            <div className="app-highlight-group">
              {SUGGESTION_FONT_SIZE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={
                    form.suggestion_font_size === option.id
                      ? "app-highlight-option selected"
                      : "app-highlight-option"
                  }
                  onClick={() =>
                    handleChange("suggestion_font_size", option.id)
                  }
                  disabled={saving}
                >
                  <span style={{ fontSize: `${option.px}px`, lineHeight: 1.2 }}>
                    {copy.fontSizes[option.id] || option.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="app-form-actions">
              <button
                type="submit"
                className="app-primary"
                disabled={saving || !isValid}
              >
                {saving ? common.saving : copy.save}
              </button>
            </div>
          </form>
        )}
      </section>
    </AppShell>
  );
};
